import React, { useState, useEffect, useCallback } from 'react';
import { adminGetAllOrders, adminGetProducts } from '../services/api';
import './SalesAnalytics.css';

interface Order {
  _id: string;
  orderReference: string;
  total: number;
  status: string;
  paymentStatus: string;
  createdAt: string;
  paidAt?: string;
  items?: OrderItem[];
}

interface OrderItem {
  productId: string;
  name: string;
  quantity: number;
  price: number;
  costPrice?: number;
}

interface Product {
  _id: string;
  name: string;
  price: number;
  costPrice: number;
  category: string;
}

interface DailySales {
  date: string;
  total: number;
  count: number;
  profit: number;
}

interface ProductSales {
  productId: string;
  name: string;
  quantity: number;
  revenue: number;
  cost: number;
  profit: number;
  profitMargin: number;
}

const SalesAnalytics: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'year' | 'all'>('month');
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [averageOrderValue, setAverageOrderValue] = useState(0);
  const [averageProfitMargin, setAverageProfitMargin] = useState(0);
  const [dailySales, setDailySales] = useState<DailySales[]>([]);
  const [topProducts, setTopProducts] = useState<ProductSales[]>([]);
  const [statusBreakdown, setStatusBreakdown] = useState<{ [key: string]: number }>({});

  const fetchOrders = useCallback(async () => {
    try {
      const response = await adminGetAllOrders();
      if (response.data.success) {
        const allOrders = response.data.orders || [];
        setOrders(allOrders);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await adminGetProducts();
      if (response.data.success) {
        setProducts(response.data.products || []);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchOrders(), fetchProducts()]).finally(() => setLoading(false));
  }, [fetchOrders, fetchProducts]);

  const getProductCostPrice = useCallback(
    (productId: string, productName: string): number => {
      // Try to find by ID first, then by name
      let product = products.find(p => p._id === productId);

      if (!product) {
        // Try to match by name (remove variation info)
        const baseName = productName.split(' (')[0];
        product = products.find(p => p.name === baseName);
      }

      return product?.costPrice || 0;
    },
    [products]
  );

  const processSalesData = useCallback(() => {
    if (orders.length === 0) return;

    const now = new Date();
    const filteredOrders = orders.filter(order => {
      if (order.paymentStatus !== 'paid') return false;
      
      const orderDate = new Date(order.paidAt || order.createdAt);
      const daysDiff = (now.getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24);
      
      switch(dateRange) {
        case 'week': return daysDiff <= 7;
        case 'month': return daysDiff <= 30;
        case 'year': return daysDiff <= 365;
        default: return true;
      }
    });

    const paidOrders = filteredOrders.filter(o => o.paymentStatus === 'paid');
    
    let revenue = 0;
    let totalItemCost = 0;
    
    // Calculate revenue and cost with product cost prices
    const productSalesMap = new Map<string, { 
      productId: string; 
      name: string; 
      quantity: number; 
      revenue: number; 
      cost: number;
    }>();
    
    paidOrders.forEach(order => {
      revenue += order.total;
      
      order.items?.forEach(item => {
        const costPrice = getProductCostPrice(item.productId, item.name);
        const itemCost = costPrice * item.quantity;
        totalItemCost += itemCost;
        
        const productId = item.productId || `prod_${item.name.replace(/\s/g, '_')}`;
        const productName = item.name.split(' (')[0];
        const existing = productSalesMap.get(productId) || { 
          productId, 
          name: productName, 
          quantity: 0, 
          revenue: 0, 
          cost: 0 
        };
        
        productSalesMap.set(productId, {
          productId,
          name: productName,
          quantity: existing.quantity + item.quantity,
          revenue: existing.revenue + (item.price * item.quantity),
          cost: existing.cost + itemCost
        });
      });
    });
    
    const profit = revenue - totalItemCost;
    
    setTotalRevenue(revenue);
    setTotalCost(totalItemCost);
    setTotalProfit(profit);
    setTotalOrders(paidOrders.length);
    setAverageOrderValue(paidOrders.length > 0 ? revenue / paidOrders.length : 0);
    setAverageProfitMargin(revenue > 0 ? (profit / revenue) * 100 : 0);

    // Process daily sales with profit
    const dailyMap = new Map<string, { total: number; count: number; cost: number }>();
    paidOrders.forEach(order => {
      const date = new Date(order.paidAt || order.createdAt).toLocaleDateString();
      let orderCost = 0;
      
      order.items?.forEach(item => {
        const costPrice = getProductCostPrice(item.productId, item.name);
        orderCost += costPrice * item.quantity;
      });
      
      const existing = dailyMap.get(date) || { total: 0, count: 0, cost: 0 };
      dailyMap.set(date, {
        total: existing.total + order.total,
        count: existing.count + 1,
        cost: existing.cost + orderCost
      });
    });
    
    const dailyArray: DailySales[] = Array.from(dailyMap.entries())
      .map(([date, data]) => ({ 
        date, 
        total: data.total, 
        count: data.count,
        profit: data.total - data.cost
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-14);

    setDailySales(dailyArray);

    // Process top products with profit
    const topProductsArray: ProductSales[] = Array.from(productSalesMap.values())
      .map(p => ({
        ...p,
        profit: p.revenue - p.cost,
        profitMargin: p.revenue > 0 ? ((p.revenue - p.cost) / p.revenue) * 100 : 0
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
    
    setTopProducts(topProductsArray);

    // Status breakdown
    const breakdown: { [key: string]: number } = {};
    orders.forEach(order => {
      breakdown[order.status] = (breakdown[order.status] || 0) + 1;
    });
    setStatusBreakdown(breakdown);
    }, [orders, dateRange, getProductCostPrice]);

  useEffect(() => {
    processSalesData();
  }, [processSalesData]);

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString()}`;
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return <div className="sales-loading">Loading sales data...</div>;
  }

  return (
    <div className="sales-analytics">
      <div className="sales-header">
        <h2>📊 Sales Analytics</h2>
        <div className="date-filter">
          <button className={dateRange === 'week' ? 'active' : ''} onClick={() => setDateRange('week')}>Last 7 Days</button>
          <button className={dateRange === 'month' ? 'active' : ''} onClick={() => setDateRange('month')}>Last 30 Days</button>
          <button className={dateRange === 'year' ? 'active' : ''} onClick={() => setDateRange('year')}>Last Year</button>
          <button className={dateRange === 'all' ? 'active' : ''} onClick={() => setDateRange('all')}>All Time</button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-icon">
            <img
              src="/money_bag_1_trans.png"
              alt="Naira Bag"
            />
          </div>
          <div className="metric-info">
            <span className="metric-value">{formatCurrency(totalRevenue)}</span>
            <span className="metric-label">Total Revenue</span>
          </div>
        </div>
        <div className="metric-card cost">
          <div className="metric-icon">📦</div>
          <div className="metric-info">
            <span className="metric-value">{formatCurrency(totalCost)}</span>
            <span className="metric-label">Total Cost of Goods</span>
          </div>
        </div>
        <div className="metric-card profit">
          <div className="metric-icon">📈</div>
          <div className="metric-info">
            <span className="metric-value profit-value">{formatCurrency(totalProfit)}</span>
            <span className="metric-label">Gross Profit</span>
            <span className="profit-badge">{formatPercentage(averageProfitMargin)} margin</span>
          </div>
        </div>
        <div className="metric-card orders">
          <div className="metric-icon">📦</div>
          <div className="metric-info">
            <span className="metric-value">{totalOrders}</span>
            <span className="metric-label completed">Completed Orders</span>
          </div>
        </div>
        <div className="metric-card average">
          <div className="metric-icon">📊</div>
          <div className="metric-info">
            <span className="metric-value">{formatCurrency(averageOrderValue)}</span>
            <span className="metric-label">Avg Order Value</span>
          </div>
        </div>
      </div>

      {/* Daily Sales Chart with Profit */}
      <div className="daily-sales-section">
        <h3>📈 Daily Performance (Last 14 days)</h3>
        <div className="daily-sales-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Orders</th>
                <th>Revenue</th>
                <th>Profit</th>
                <th>Margin</th>
              </tr>
            </thead>
            <tbody>
              {dailySales.length > 0 ? dailySales.map(day => (
                <tr key={day.date}>
                  <td>{day.date}</td>
                  <td>{day.count}</td>
                  <td>{formatCurrency(day.total)}</td>
                  <td className={day.profit >= 0 ? 'profit-positive' : 'profit-negative'}>
                    {formatCurrency(day.profit)}
                  </td>
                  <td>
                    <span className={`margin-badge ${day.total > 0 && (day.profit / day.total) * 100 >= 30 ? 'high' : 'low'}`}>
                      {day.total > 0 ? formatPercentage((day.profit / day.total) * 100) : '0%'}
                    </span>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={5} style={{ textAlign: 'center' }}>No sales data available</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Products with Profit Analysis */}
      <div className="top-products-section">
        <h3>🏆 Top Selling Products</h3>
        <div className="top-products-table">
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Quantity Sold</th>
                <th>Revenue</th>
                <th>Cost</th>
                <th>Profit</th>
                <th>Margin</th>
              </tr>
            </thead>
            <tbody>
              {topProducts.length > 0 ? topProducts.map(product => (
                <tr key={product.productId}>
                  <td>
                    {product.name}
                    <span className="product-id-sub">{product.productId.slice(-8)}</span>
                  </td>
                  <td>{product.quantity}</td>
                  <td>{formatCurrency(product.revenue)}</td>
                  <td>{formatCurrency(product.cost)}</td>
                  <td className={product.profit >= 0 ? 'profit-positive' : 'profit-negative'}>
                    {formatCurrency(product.profit)}
                  </td>
                  <td>
                    <span className={`margin-badge ${product.profitMargin >= 30 ? 'high' : product.profitMargin >= 15 ? 'medium' : 'low'}`}>
                      {formatPercentage(product.profitMargin)}
                    </span>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={6} style={{ textAlign: 'center' }}>No product sales data available</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Status Breakdown */}
      <div className="status-section">
        <h3>📋 Order Status Distribution</h3>
        <div className="status-grid">
          {Object.entries(statusBreakdown).map(([status, count]) => (
            <div key={status} className={`status-item ${status}`}>
              <span className="status-name">{status.toUpperCase()}</span>
              <span className="status-count">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Export Buttons */}
      <div className="export-section">
        <button 
          className="export-btn"
          onClick={() => {
            const csvData = dailySales.map(day => 
              `${day.date},${day.count},${day.total},${day.profit},${day.total > 0 ? ((day.profit / day.total) * 100).toFixed(1) : 0}`
            ).join('\n');
            const blob = new Blob([`Date,Orders,Revenue,Profit,Margin(%)\n${csvData}`], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `sales_report_${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          📥 Export Sales Report (CSV)
        </button>
        <button 
          className="export-btn profit-export"
          onClick={() => {
            const csvData = topProducts.map(product => 
              `${product.productId},${product.name},${product.quantity},${product.revenue},${product.cost},${product.profit},${product.profitMargin.toFixed(1)}`
            ).join('\n');
            const blob = new Blob([`Product ID,Product Name,Quantity,Revenue,Cost,Profit,Profit Margin(%)\n${csvData}`], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `profit_analysis_${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          📊 Export Profit Analysis (CSV)
        </button>
      </div>
    </div>
  );
};

export default SalesAnalytics;